/**
 * Form Validators Utility
 * 
 * Collection of custom Angular form validators used throughout the MacSafety web
 * application for form validation. These validators provide specialized validation
 * logic beyond Angular's built-in validators, ensuring data integrity and user
 * experience consistency across all forms.
 * 
 * Key Validators:
 * - checkboxValidator: Ensures specific number of checkboxes are checked in a group
 * - phoneNumberValidator: Validates US phone number formats with strict formatting
 * - flexiblePhoneValidator: International phone number validation with configurable digit limits
 * - simpleCheckboxValidator: Simplified checkbox validation for form groups
 * - getRequiredFields: Analyzes form groups to identify required field validation status
 * 
 * Validation Categories:
 * - Checkbox Selection: Ensures minimum/maximum checkbox selections within groups
 * - Phone Numbers: Multiple phone number format validation with international support
 * - Form Analysis: Utility functions for analyzing form validation states
 * 
 * Primary Use Cases:
 * - Individual form components during FormGroup creation and validation setup
 * - Form input controls requiring specialized validation beyond standard Angular validators
 * - Checkbox groups requiring specific selection constraints
 * - Phone number fields requiring format and digit validation
 * - Form analysis and debugging for validation state inspection
 * 
 * Components that utilize these validators:
 * - All individual form components (EmployeeInjuryReport, HotWorkPermit, etc.)
 * - BaseFormComponent: For common validation patterns
 * - FormLayoutComponent: For form-level validation setup
 * - Any component requiring custom form validation logic
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { AbstractControl, FormGroup, Validators, ValidatorFn } from '@angular/forms';

/**
 * Custom form validator to ensure a specific number of checkboxes are checked within a group
 * Useful for forms requiring specific checkbox selection constraints (e.g., safety checklists)
 * 
 * @param checkboxKeys - Array of form control names that should be checked
 * @param minSelections - Minimum number of selections required (default: 1)
 * @param maxSelections - Maximum number of selections allowed (default: 0 = unlimited)
 * @returns Validator function that returns null if valid, or an error object if invalid
 */
export function checkboxValidator(checkboxKeys: Array<string>, minSelections: number = 1, maxSelections: number = 0) {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const formGroup = control as FormGroup;
    
    // Count how many specified checkboxes are checked
    const checkedCount = checkboxKeys.filter(key => {
      const control = formGroup.get(key);
      return control?.value === true;
    }).length;

    // Validate minimum selection requirement
    if (checkedCount < minSelections) {
      return { 
        minSelections: {
          required: minSelections,
          actual: checkedCount
        }
      };
    }

    // Validate maximum selection limit (if specified)
    if (maxSelections > 0 && checkedCount > maxSelections) {
      return { 
        maxSelections: {
          required: maxSelections,
          actual: checkedCount
        }
      };
    }

    return null;
  };
}

/**
 * Custom validator for phone number format validation
 * Accepts various US phone number formats: (555) 123-4567, 555-123-4567, 555.123.4567, 5551234567
 * Ensures exactly 10 digits with optional formatting characters
 * 
 * @returns Validator function that returns null if valid, or an error object if invalid
 */
export function phoneNumberValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!control.value) {
      return null; // Don't validate empty values (let required validator handle that)
    }

    // Remove all non-digit characters to check if we have 10 digits
    const digitsOnly = control.value.replace(/\D/g, '');
    
    // Must be exactly 10 digits (US phone number)
    if (digitsOnly.length !== 10) {
      return { phoneNumber: { value: control.value, message: 'Phone number must be 10 digits' } };
    }

    // Optional: More strict format validation with regex
    const phoneRegex = /^(\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})$/;
    if (!phoneRegex.test(control.value)) {
      return { phoneNumber: { value: control.value, message: 'Please enter a valid phone number format' } };
    }

    return null; // Valid
  };
}

/**
 * Alternative phone validator with more flexible international support
 * Configurable digit limits for different country phone number requirements
 * Less strict than phoneNumberValidator, focusing only on digit count
 * 
 * @param minDigits - Minimum number of digits required (default: 10)
 * @param maxDigits - Maximum number of digits allowed (default: 15)
 * @returns Validator function that returns null if valid, or an error object if invalid
 */
export function flexiblePhoneValidator(minDigits: number = 10, maxDigits: number = 15): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!control.value) {
      return null; // Don't validate empty values
    }

    // Extract only digits for validation
    const digitsOnly = control.value.replace(/\D/g, '');
    
    // Validate digit count within specified range
    if (digitsOnly.length < minDigits || digitsOnly.length > maxDigits) {
      return { 
        phoneNumber: { 
          value: control.value, 
          message: `Phone number must be between ${minDigits} and ${maxDigits} digits` 
        } 
      };
    }

    return null;
  };
}

/**
 * Simple checkbox validator that works directly with form group values
 * Simplified version of checkboxValidator that doesn't require specifying checkbox keys
 * Automatically counts all boolean true values in the form group
 * 
 * @param minSelections - Minimum number of selections required (default: 1)
 * @param maxSelections - Maximum number of selections allowed (default: 0 = unlimited)
 * @returns Validator function that returns null if valid, or an error object if invalid
 */
export function simpleCheckboxValidator(minSelections: number = 1, maxSelections: number = 0): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const formGroup = control as FormGroup;
    if (!formGroup) return null;

    // Count how many checkboxes are checked (boolean true values)
    const checkedCount = Object.values(formGroup.value).filter(value => value === true).length;

    // Validate minimum selection requirement
    if (checkedCount < minSelections) {
      return { 
        minSelections: {
          required: minSelections,
          actual: checkedCount
        }
      };
    }

    // Validate maximum selection limit (if specified)
    if (maxSelections > 0 && checkedCount > maxSelections) {
      return { 
        maxSelections: {
          required: maxSelections,
          actual: checkedCount
        }
      };
    }

    return null;
  };
}

// Add more shared validators here as needed 

/**
 * Interface defining the structure for required fields mapping
 * Maps field names to their required validation status
 */
export interface RequiredFieldsMap {
  [key: string]: boolean;  // Field name -> is required
}

/**
 * Analyzes a form group to identify which fields have required validation
 * Recursively processes nested form groups to build a complete required fields map
 * Useful for debugging form validation states and building dynamic validation summaries
 * 
 * @param formGroup - The form group to analyze for required field validation
 * @returns Object mapping field names to their required validation status
 */
export function getRequiredFields(formGroup: FormGroup): RequiredFieldsMap {

  const requiredFields: RequiredFieldsMap = {};
  
  /**
   * Recursively processes form groups to identify required fields
   * Handles nested form groups by building dot-notation field names
   * 
   * @param group - The form group to process
   * @param prefix - The prefix for nested field names (e.g., 'section1.fieldName')
   */
  function processFormGroup(group: FormGroup, prefix: string = '') {
    Object.keys(group.controls).forEach(controlName => {
      const control = group.controls[controlName];
      const fullControlName = prefix ? `${prefix}.${controlName}` : controlName;
      
      if (control instanceof FormGroup) {
        // Recursively process nested form groups
        processFormGroup(control, controlName);
      } else {
        // Process regular form controls and check for required validation
        requiredFields[controlName] = control.hasError('required');
      }
    });
  }

  processFormGroup(formGroup);

  return requiredFields;
}